import { GestureType } from '../../types';

/**
 * Debounce state for a single gesture
 */
interface DebounceState {
  isActive: boolean;           // Whether gesture is currently being held
  holdStartTime: number | null; // When gesture started being held
  lastTriggerTime: number | null; // When gesture last triggered
  hasTriggered: boolean;        // Whether gesture has triggered in current hold
}

/**
 * GestureDebouncer prevents rapid gesture firing
 * Validates Requirements 3.3.5
 * 
 * Rules:
 * - Gesture must be held for HOLD_DURATION ms to trigger
 * - After trigger, COOLDOWN_PERIOD ms must pass before same gesture can trigger again
 */
export class GestureDebouncer {
  private static readonly HOLD_DURATION = 500; // ms
  private static readonly COOLDOWN_PERIOD = 1000; // ms
  
  private debounceStates: Map<GestureType, DebounceState>;
  
  constructor() {
    this.debounceStates = new Map();
    this.initializeStates();
  }
  
  /**
   * Initialize debounce state for all gesture types
   */
  private initializeStates(): void {
    for (const gestureType of Object.values(GestureType)) {
      this.debounceStates.set(gestureType, {
        isActive: false,
        holdStartTime: null,
        lastTriggerTime: null,
        hasTriggered: false
      });
    }
  }
  
  /**
   * Check if a gesture should trigger
   * @param gestureType Type of gesture
   * @param isDetected Whether gesture is currently detected
   * @param timestamp Current timestamp
   * @returns True if gesture should trigger
   */
  shouldTrigger(gestureType: GestureType, isDetected: boolean, timestamp: number): boolean {
    const state = this.debounceStates.get(gestureType);
    if (!state) return false;
    
    // If gesture is not detected, reset state
    if (!isDetected) {
      state.isActive = false;
      state.holdStartTime = null;
      state.hasTriggered = false;
      return false;
    }
    
    // Gesture is detected
    if (!state.isActive) {
      // Start tracking hold
      state.isActive = true;
      state.holdStartTime = timestamp;
      state.hasTriggered = false;
    }
    
    // Check if in cooldown period
    if (state.lastTriggerTime !== null) {
      const timeSinceLastTrigger = timestamp - state.lastTriggerTime;
      if (timeSinceLastTrigger < GestureDebouncer.COOLDOWN_PERIOD) {
        return false;
      }
    }
    
    // Check if held long enough and hasn't triggered yet
    if (state.holdStartTime !== null && !state.hasTriggered) {
      const holdDuration = timestamp - state.holdStartTime;
      if (holdDuration >= GestureDebouncer.HOLD_DURATION) {
        state.hasTriggered = true;
        state.lastTriggerTime = timestamp;
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * Reset debounce state for a specific gesture
   * @param gestureType Type of gesture to reset
   */
  resetGesture(gestureType: GestureType): void {
    const state = this.debounceStates.get(gestureType);
    if (state) {
      state.isActive = false;
      state.holdStartTime = null;
      state.lastTriggerTime = null;
      state.hasTriggered = false;
    }
  }
  
  /**
   * Reset all gesture debounce states
   */
  resetAll(): void {
    for (const gestureType of Object.values(GestureType)) {
      this.resetGesture(gestureType);
    }
  }
  
  /**
   * Get the hold duration requirement
   */
  static getHoldDuration(): number {
    return GestureDebouncer.HOLD_DURATION;
  }
  
  /**
   * Get the cooldown period
   */
  static getCooldownPeriod(): number {
    return GestureDebouncer.COOLDOWN_PERIOD;
  }
}
